import { FunctionComponent, JSX, useEffect, useState } from 'react'
import { EventRegularOverlap, OverlappingSlot, ResultCreateRegularSlot } from '@/types/Slots.ts'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { Close } from '@mui/icons-material'
import styles from './overlapsRegularSlotsDialog.module.scss'
import Check from '@mui/icons-material/Check'
import moment from 'moment/moment'
import OverlapsElement from './overlapsElement.tsx'
import { FetchResult, useMutation } from '@apollo/client'
import { CREATE_MULTIPLE_REGULAR_SLOT } from '@/modules/API/slot/regularSlotHandler.ts'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { ArrowRightIcon } from '@mui/x-date-pickers'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface OverlapsRegularSlotsDialogProps {
    handleClose: () => void
    open: boolean
    overlapsSlots: ResultCreateRegularSlot
    closeParentDialog: () => void
    regularSlotStart: string
    regularSlotEnd: string
    daycareId: number
    place: string
    refetchData: () => void
}

const OverlapsRegularSlotsDialog: FunctionComponent<OverlapsRegularSlotsDialogProps> = ({
    handleClose,
    open,
    overlapsSlots,
    closeParentDialog,
    regularSlotStart,
    regularSlotEnd,
    daycareId,
    place,
    refetchData
}: OverlapsRegularSlotsDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [events, setEvents] = useState<EventRegularOverlap[]>([])

    const [selectedEvents, setSelectedEvents] = useState<number[]>([])

    function handleCheck(number: number, checked: boolean): void {
        const tempSelectedEvents: number[] = selectedEvents
        setSelectedEvents([])
        if (checked) {
            setSelectedEvents([...tempSelectedEvents, number])
        } else {
            setSelectedEvents(tempSelectedEvents.filter((event: number) => event !== number))
        }
    }

    useEffect(() => {
        const events: EventRegularOverlap[] = [
            ...overlapsSlots.occasionalSlots.map(
                (slot: OverlappingSlot): EventRegularOverlap => buildEventFromOverlappingSlot(slot)
            ),
            ...overlapsSlots.regularSlots.map(
                (slot: OverlappingSlot): EventRegularOverlap => buildEventFromOverlappingSlot(slot)
            ),
            ...overlapsSlots.requestSlots.map(
                (slot: OverlappingSlot): EventRegularOverlap => buildEventFromOverlappingSlot(slot)
            )
        ]
        setEvents(events)
        setSelectedEvents(events.map((event: EventRegularOverlap) => event.slotId))
    }, [overlapsSlots])

    const closeAllDialogs: () => void = (): void => {
        refetchData()
        closeParentDialog()
        handleClose()
    }

    const [createMultipleRegularSlots] = useMutation(CREATE_MULTIPLE_REGULAR_SLOT, {
        onError: () => {
            closeAllDialogs()

            showNotificationError(t('errorCreatingRegularSlots'))
        }
    })

    function handleConfirm(): void {
        const eventToCreate: EventRegularOverlap[] = events.filter((event: EventRegularOverlap): boolean =>
            selectedEvents.includes(event.slotId)
        )

        const eventByChild: Map<number, EventRegularOverlap[]> = new Map<number, EventRegularOverlap[]>()
        eventToCreate.forEach((event: EventRegularOverlap): void => {
            if (event.childId) {
                if (eventByChild.has(event.childId)) {
                    eventByChild.get(event.childId)!.push(event)
                } else {
                    eventByChild.set(event.childId, [event])
                }
            }
        })

        Promise.all(
            Array.from(eventByChild.entries()).map(
                ([childId, events]: [number, EventRegularOverlap[]]): Promise<FetchResult> =>
                    createMultipleRegularSlots({
                        variables: {
                            daycareId: daycareId,
                            childIds: [childId],
                            dateList: events.map((event: EventRegularOverlap): string =>
                                moment(event.date).format('DD-MM-YYYY')
                            ),
                            overwrite: true,
                            start: regularSlotStart,
                            end: regularSlotEnd,
                            placeId: place === '' ? undefined : Number(place)
                        }
                    })
            )
        ).then(() => {
            closeAllDialogs()
            showNotificationSuccess('success')
        })
    }

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('information')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.SecondContainer}>
                    <p className={styles.Text}>
                        {t('slotWasInConflict')} <span className={styles.TextHours}>{regularSlotStart}</span>{' '}
                        {t('to').toLowerCase()} <span className={styles.TextHours}>{regularSlotEnd}</span>
                        {'.'}
                    </p>
                </div>
                {overlapsSlots.otherSlots.length > 0 && (
                    <>
                        <div className={styles.BreakLine}></div>
                        <div className={styles.LeftContainer}>
                            <p className={styles.Text}>
                                {t('thereIs')} {overlapsSlots.otherSlots.length}
                                {t('slotsInOtherDaycare')}
                            </p>
                        </div>
                        <div className={styles.LeftContainer}>
                            {overlapsSlots.otherSlots.map(
                                (other: OverlappingSlot, index: number): JSX.Element => (
                                    <div key={index} className={styles.ListElement}>
                                        <ArrowRightIcon className={styles.Icon} />
                                        <p className={styles.Text}>
                                            {format(parse(other.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                                locale: getLocalFromLanguage()
                                            })}
                                        </p>
                                    </div>
                                )
                            )}
                        </div>
                        <div className={styles.LeftContainer}>
                            <p className={styles.Text}>{t('youCantOverwrite')}</p>
                        </div>
                    </>
                )}
                {events.length > 0 ? (
                    <>
                        <div className={styles.BreakLine}></div>
                        <div className={styles.Container}>
                            {events.map(
                                (event: EventRegularOverlap, index: number): JSX.Element => (
                                    <OverlapsElement
                                        key={index}
                                        event={event}
                                        checked={selectedEvents.includes(event.slotId)}
                                        handleCheck={handleCheck}
                                    />
                                )
                            )}
                        </div>
                        <div className={styles.BreakLine}></div>
                        <div className={styles.SecondContainer}>
                            <p className={styles.Text}>{t('areYouSureOverwrite')}</p>
                        </div>
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'custom'}
                                customIcon={<Check />}
                                customText={t('yes')}
                                onClick={handleConfirm}
                                className={styles.Button}
                            />
                            <ButtonTextIcon
                                type={'custom'}
                                customIcon={<Close />}
                                customText={t('no')}
                                onClick={closeAllDialogs}
                                className={styles.Button}
                            />
                        </div>
                    </>
                ) : (
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon type={'close'} onClick={closeAllDialogs} className={styles.Button} />
                    </div>
                )}
            </DialogBody>
        </Dialog>
    )
}

export default OverlapsRegularSlotsDialog

function buildEventFromOverlappingSlot(slot: OverlappingSlot): EventRegularOverlap {
    const date: string = moment(slot.date, 'DD-MM-YYYY').format('DD/MM/YYYY')
    const start: string = moment(slot.start, 'HH:mm:ss').format('HH:mm')
    const end: string = moment(slot.end, 'HH:mm:ss').format('HH:mm')

    const title: string =
        `${slot.childName ? ' - ' + slot.childName : ''}` +
        `${slot.childComplement ? ' - ' + slot.childComplement : ''}` +
        `${slot.placeName ? ' - ' + slot.placeName : ''}`

    return {
        title: `${date} - ${start} - ${end} ${title}`,
        date: moment(slot.date, 'DD-MM-YYYY').toDate(),
        start: slot.start,
        end: slot.end,
        slotId: slot.slotId,
        type: slot.slotType,
        childId: slot.childId
    }
}
